import React, { useEffect, useState } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { FloatLabel, InputText, ProgressBar, ProgressSpinner, SCButton, SubmitHandler, Controller } from "../../Common/Imports";
import { ApiResponse, User_EXT } from '../../Models/Classes';
import { ValidateEmail } from '../../Common/Utility/ValidateEmail';

interface UserNewProps {
    email: string;
    OnSave?: () => void;
}

const UserNew: React.FC<UserNewProps> = ({ email, OnSave }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [userData, setUserData] = useState<User_EXT | undefined>();
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<User_EXT>();
    const AxiosHelper = useAxios();
    const [LoaderSendResetData, setLoaderSendResetData] = useState<boolean>(false);

    // Fetch user data when component mounts
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await AxiosHelper.get<ApiResponse<User_EXT>>(`user/GetUser/` + email);
                setUserData(response.data.data);
                reset(response.data.data);
            } catch (error) {
                console.error('Error fetching user data:', error);
                showToast('error', 'Error', t("Common.Toasts.Fetch.Error"));
            }
        };

        fetchUser();
    }, [reset, AxiosHelper, t, showToast, email]);

    const onSubmit: SubmitHandler<User_EXT> = async (data: User_EXT) => {
        data.userName = data.email;
        try {
            await AxiosHelper.post(`user/UpdateUser`, data);
            showToast('success', 'Success', t("Common.Toasts.New.Success"));
            if (OnSave) {
                OnSave();
            }
        } catch (error: unknown) {
                console.log(error);
                showToast('error', 'Error', t("Common.Toasts.New.Error"));
        }
    };

    if (!userData) {
        return <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
    }

    const sendReset = async () => {
        setLoaderSendResetData(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<User_EXT>>(`Auth/ForgotPassword/` + userData.email);
            if (resp.data.code == 0) {
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.Update.Error"));
        } finally {
            setLoaderSendResetData(false);
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <div className="row mb-3 mt-3">
                <div className="col-md-6 mb-4">
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: t("User.Edit.Name") + " " + t("Auth.Register.Form.Required") }}
                        render={({ field }) => (
                            <FloatLabel>
                                <InputText
                                    id="UserName"
                                    {...field}
                                    className={classNames({ 'p-invalid': errors.name })}
                                />
                                <label htmlFor="UserName">{t("User.Edit.Name")}</label>
                            </FloatLabel>
                        )}
                    />
                    {errors.name && (
                        <small className="p-error">
                            {errors.name.message}
                        </small>
                    )}
                </div>

                <div className="col-md-6 mb-4">
                    <Controller
                        name="surname"
                        control={control}
                        rules={{ required: t("User.Edit.Surname") + " " + t("Auth.Register.Form.Required") }}
                        render={({ field }) => (
                            <FloatLabel>
                                <InputText
                                    id="UserSurname"
                                    {...field}
                                    className={classNames({ 'p-invalid': errors.surname })}
                                />
                                <label htmlFor="UserSurname">{t("User.Edit.Surname")}</label>
                            </FloatLabel>
                        )}
                    />
                    {errors.surname && (
                        <small className="p-error">
                            {errors.surname.message}
                        </small>
                    )}
                </div>

                <div className="col-md-12 mb-4">
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: t("User.Edit.Email") + " " + t("Common.Form.Required"),
                            validate: (value) =>
                                value && ValidateEmail(value) || t("User.Edit.Email") + " " + t("Common.Form.Invalid")
                        }}
                        render={({ field }) => (
                            <FloatLabel>
                                <InputText
                                    id="email"
                                    {...field}
                                    className={classNames({ 'p-invalid': errors.email })}
                                />
                                <label htmlFor="UserEmail">{t("User.Edit.Email")}</label>
                            </FloatLabel>
                        )}
                    />
                    {errors.email && (
                        <small className="p-error">
                            {errors.email.message}
                        </small>
                    )}
                </div>
                <div className="row">
                    <div className="col-md-6 d-flex justify-content-between">
                        {
                            LoaderSendResetData ?
                                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration=".5s" />
                                :
                                <SCButton Type="button" Label={t("User.Edit.SendResetPasswordMail")} Name="Mail" OnClick={sendReset} />
                        }
                    </div>
                    <div className="col-md-6">
                        <SCButton Type="submit" Label={t("Common.Buttons.Save")} Name="Save" />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default UserNew;
